import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Collapse, UnmountClosed} from 'react-collapse';
import {useDispatch, useSelector} from 'react-redux';
import {Alert, Form, Input, Select, Spin} from 'antd';
import {StyledAddPaymentForm, StyledAddPaymentFormAlertContainer} from './StyledAddPaymentForm';
import VendorSelectorWithAuth from '../VendorSelectorWithAuth';
import EuroInput from '../../../EuroInput';
import InvoiceUploader from '../InvoiceUploader';
import VendorPaymentsList from '../VendorPaymentsList';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import {formHelpers, scaHelpers, subscriptionsHelpers, vendorsHelpers} from '../../../../utils/helpers';
import {validation} from '../../../../utils/validation';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {subscriptionActions} from '../../../../state/actions/subscription.actions';
import {vendorsActions} from '../../../../state/actions/vendors.actions';
import {SCAActionsConstants} from '../../../../constants';
import {useFormHelpers} from '../../../../hooks';

const {Item} = Form;

const initialFormValues = {
  description: '',
  invoice_id: undefined,
  price: undefined,
  subscription_id: undefined,
  vendor_local_id: undefined
}

const defaultData = {data: [], loading: false};

const {PAYMENT_CREATE_ACTION, BENEFICIARY_VALIDATE_ACTION} = SCAActionsConstants;

const AddPaymentForm = React.forwardRef(({
  additionalFormValues = {},
  initialFormData = {},
  beforeSubmit,
  clearAfterSubmit = true,
  onBeneficiaryValidationChange,
  onError,
  onValuesChange,
  onSuccess,
  size = 'full',
  showInvoiceButton = true,
  vendorSelectorProps = {},
  ...rest
}, ref) => {
  const [t] = useTranslation(['main', 'vendors']);
  const [form] = Form.useForm();
  const [authWindowProps, setAuthWindowProps] = useState({loading: false, open: false});
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [subscriptions, setSubscriptions] = useState(defaultData);
  const [payments, setPayments] = useState(defaultData);
  const [activeAuthAction, setActiveAuthAction] = useState(null);
  const [beneficiary, setBeneficiary] = useState(null);
  const [beneficiaryValidationDetails, setBeneficiaryValidationDetails] = useState({
    alertType: 'warning',
    beneficiaryValidationId: null,
    isValid: false,
    loading: false,
    message: null,
    show: false
  });

  const vendorId = Form.useWatch('vendor_local_id', form);

  const {placeholderEnter, placeholderSelect} = useFormHelpers();

  const walletId = useSelector(state => state.banking?.companyDetails?.wallet_id);

  const dispatch = useDispatch();

  const validationRules = useMemo(() => ({
    required: validation.required(t),
  }), [t]);

  const requiredItemProps = useMemo(() => ({rules: validationRules.required, required: true}), [validationRules.required]);

  const productOptions = useMemo(() => subscriptionsHelpers.getSubscriptionOptions(subscriptions?.data || []), [subscriptions?.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMultipleProducts = useMemo(() => productOptions.length > 1, [productOptions]);

  const authModalProps = useMemo(() => {
    const title = {
      [PAYMENT_CREATE_ACTION]: `${t('new')} ${t('payment')}`,
      [BENEFICIARY_VALIDATE_ACTION]: t('vendors:beneficiaryValidation')
    }[activeAuthAction] || undefined;
    return { title };
  }, [activeAuthAction, t]);

  useEffect(() => {
    const valid = beneficiaryValidationDetails.isValid && !beneficiaryValidationDetails.loading;
    onBeneficiaryValidationChange?.(valid);
  }, [beneficiaryValidationDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (productOptions.length === 1) {
      form.setFieldValue('subscription_id', productOptions[0].value);
    }
  }, [form, productOptions]);

  useEffect(() => {
    if (error) setError(null);
  }, [vendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSubscriptions = (id) => {
    if (!id) return;

    setSubscriptions({...subscriptions, loading: true});
    dispatch(subscriptionActions.getVendorSubscriptions({
      id,
      successCallback: ({subscriptions: data}) => setSubscriptions({data, loading: false}),
      errorCallback: () => setSubscriptions(defaultData)
    }));
  }

  const loadPayments = (id) => {
    if (!id) return;

    setPayments({...payments, loading: true});
    dispatch(vendorsActions.getVendorPayments({
      id,
      query: { sort_order: 'desc' },
      successCallback: (data) => setPayments({data, loading: false}),
      errorCallback: () => setPayments(defaultData)
    }));
  }

  useEffect(() => {
    form.setFieldsValue(initialFormData);
  }, [initialFormData, form]);

  useEffect(() => {
    form.setFieldValue('product_id', undefined);

    if (vendorId) {
      loadSubscriptions(vendorId);
      loadPayments(vendorId);
    } else {
      setAttachments([]);
      setPayments(defaultData);
      setSubscriptions(defaultData);
    }
  }, [vendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadAttachment = (expenseId, onComplete) => {
    if (!expenseId || attachments.some(att => att.id === expenseId)) {
      onComplete?.();
      return;
    }

    dispatch(expenseActions.getExpenseAttachment({
      id: expenseId,
      successCallback: (data) => {
        const contentType = data['content-type'];
        const attachment = {
          contentType,
          data,
          id: expenseId,
          file: `data:${contentType};base64,${data.file}`,
          name: data.filename
        };
        setAttachments([...attachments, attachment]);
        onComplete?.();
      },
      errorCallback: () => {
        onComplete?.();
      }
    }));
  }

  const handleSubmit = (formData) => {
    const data = {
      ...formData,
      ...additionalFormValues,
      wallet_id: walletId,
      beneficiary_validation_id: beneficiaryValidationDetails.beneficiaryValidationId
    };

    beforeSubmit?.();

    setIsDisabled(true);

    storeActiveAuthAction(PAYMENT_CREATE_ACTION);

    dispatch(vendorsActions.createPayment({
      data,
      headers: scaHelpers.getAuthHeaders(PAYMENT_CREATE_ACTION),
      successCallback: (response) => {
        setIsDisabled(false);
        onSuccess?.(response);

        if (clearAfterSubmit) {
          form.resetFields();
          form.setFieldsValue(initialFormData);
        }
      },
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
          errorCallback: (err) => {
            const fields = formHelpers.getFormServerErrorFields(err.response.data);
            form.setFields(fields);
            setIsDisabled(false);
          }
        });
      }
    }));
  }

  const storeActiveAuthAction = (action) => {
    if (action === activeAuthAction) return;
    setActiveAuthAction(action);
  }

  const handleCloseAuthModal = () => {
    const operationActions = {
      [PAYMENT_CREATE_ACTION]: () => {
        onError?.();
        setIsDisabled(false);
      },
    }
    setAuthWindowProps({...authWindowProps, open: false});
    operationActions[activeAuthAction]?.();
  }

  const handleSuccessAuth = () => {
    const operationActions = {
      [PAYMENT_CREATE_ACTION]: () => form.submit(),
      [BENEFICIARY_VALIDATE_ACTION]: () => handleBeneficiaryLoaded(beneficiary),
    }
    setAuthWindowProps({...authWindowProps, open: false});
    operationActions[activeAuthAction]?.();
  }

  const handleUploadInvoice = (invoice) => form.setFieldValue('invoice_id', invoice?.id);

  const handleBeneficiaryLoaded = (beneficiary) => {
    vendorSelectorProps?.onBeneficiaryLoaded?.(beneficiary);

    if (!beneficiary) return;

    setBeneficiary(beneficiary);
    storeActiveAuthAction(BENEFICIARY_VALIDATE_ACTION);

    setBeneficiaryValidationDetails(prev => ({ ...prev, isValid: false, loading: true }));

    dispatch(vendorsActions.validateBeneficiary({
      data: {
        beneficiary_name: beneficiary?.name,
        beneficiary_iban: beneficiary?.clear_iban,
        recurrence_type: 'default'
      },
      headers: scaHelpers.getAuthHeaders(BENEFICIARY_VALIDATE_ACTION),
      successCallback: (validationDetails) => {
        setBeneficiaryValidationDetails({
          ...vendorsHelpers.getBeneficiaryValidationDetails({t, validationDetails}),
          loading: false,
          show: true
        });
      },
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
          errorCallback: (err) => {
            setBeneficiaryValidationDetails({
              alertType: 'error',
              beneficiaryValidationId: null,
              isValid: false,
              loading: false,
              message: err?.response?.data?.detail || t('vendors:error.beneficiaryValidationError'),
              show: true
            });
          }
        });
      }
    }))
  };

  const handleBeneficiaryChange = (beneficiaryProps) => {
    const beneficiary = beneficiaryProps?.beneficiary;
    if (!beneficiary) {
      setBeneficiaryValidationDetails(prev => ({ ...prev, show: false, isValid: false }));
    }
    vendorSelectorProps?.onBeneficiaryChange?.(beneficiaryProps);
  }

  return (
    <StyledAddPaymentForm
      size={size}
      {...rest}
    >
      <UnmountClosed isOpened={payments.data.length}>
        <VendorPaymentsList
          attachments={attachments}
          className='vendor-payments-list'
          onAttachmentLoad={handleLoadAttachment}
          payments={payments.data}
        />
      </UnmountClosed>

      <Form
        className={isDisabled ? 'disabled-form' : ''}
        disabled={isDisabled}
        initialValues={{...initialFormValues, ...additionalFormValues}}
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        onValuesChange={onValuesChange}
        ref={ref}
      >
        <Item
          {...requiredItemProps}
          label={t('vendor')}
          name='vendor_local_id'
        >
          <VendorSelectorWithAuth
            {...vendorSelectorProps}
            onBeneficiaryLoaded={handleBeneficiaryLoaded}
            onBeneficiaryChange={handleBeneficiaryChange}
            vendorFormSize='short'
            selectProps={{
              className: 'vendor-selector',
              placeholder: placeholderSelect('vendor')
            }}
          />
        </Item>

        <UnmountClosed isOpened={Boolean(beneficiaryValidationDetails.show)} theme={{ collapse: 'alert-collapse-root' }}>
          <StyledAddPaymentFormAlertContainer>
            <Spin spinning={beneficiaryValidationDetails.loading}>
              <Alert
                message={beneficiaryValidationDetails.message}
                type={beneficiaryValidationDetails.alertType}
                showIcon={true}
              />
            </Spin>
          </StyledAddPaymentFormAlertContainer>
        </UnmountClosed>

        <UnmountClosed isOpened={hasMultipleProducts}>
          <Item
            {...requiredItemProps}
            label={t('product')}
            name='product_id'
          >
            <Select
              loading={subscriptions.loading}
              disabled={isDisabled ? true : !vendorId}
              optionFilterProp='optionLabel'
              options={productOptions}
              placeholder={placeholderSelect('product')}
              size='large'
              showSearch
            />
          </Item>
        </UnmountClosed>

        <Item
          {...requiredItemProps}
          label={t('amount')}
          name='price'
        >
          <EuroInput placeholder={placeholderEnter('amount')} />
        </Item>
        <Item
          className={hasMultipleProducts && 'w-100'}
          label={t('reason')}
          name='description'
        >
          <Input
            placeholder={placeholderSelect('reason')}
            size='large'
          />
        </Item>

        {showInvoiceButton && (
          <Item name='invoice_id'>
            <InvoiceUploader onChange={handleUploadInvoice} />
          </Item>
        )}

        <Collapse isOpened={error}>
          <Alert message={error} type='error' showIcon />
        </Collapse>
      </Form>

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={authModalProps}
        handleCancel={handleCloseAuthModal}
        onSuccess={handleSuccessAuth}
        operationName={activeAuthAction}
      />
    </StyledAddPaymentForm>
  );
});

AddPaymentForm.propTypes = {
  additionalFormValues: PropTypes.object,
  beforeSubmit: PropTypes.func,
  clearAfterSubmit: PropTypes.bool,
  initialFormData: PropTypes.object,
  onBeneficiaryValidationChange: PropTypes.func,
  onValuesChange: PropTypes.func,
  onSuccess: PropTypes.func,
  size: PropTypes.oneOf(['full', 'half']),
  showInvoiceButton: PropTypes.bool,
  vendorSelectorProps: PropTypes.object
}

export default AddPaymentForm;
